"use client";

import { useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { useInView } from "@/hooks/useInView";
import { ScrollReveal } from "./ScrollReveal";
import { LandingSectionHeader } from "./LandingSectionHeader";

const before = ["CodeWalker loop", "Manual folder cleanup", "Meta file guessing", "Discord utility hunting"];
const after  = ["Browser preview",  "Generated structure",  "Guided meta editor",  "One subscription, every tool"];

export function LandingBeforeAfter() {
  const [sectionRef, isVisible] = useInView<HTMLElement>(0.08);
  const [beforeHovered, setBeforeHovered] = useState(false);
  const [afterHovered,  setAfterHovered]  = useState(false);

  return (
    <section ref={sectionRef} className="bg-[hsl(var(--lb-bg-soft))] py-8 sm:py-14">
      <div className="lb-container">
        <ScrollReveal>
          <LandingSectionHeader
            eyebrow="The Future of Asset Creation"
            title="Less tool switching. More shipping."
            copy="Stop chaining local utilities and Discord-shared scripts. Run the same work in one browser tab."
            align="center"
          />
        </ScrollReveal>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {/* ── Before card ─────────────────────────────────────────── */}
          <div
            className="lb-line-card group relative overflow-hidden p-8 sm:p-10 transition-all duration-500 ease-out"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible
                ? beforeHovered ? "translateY(-4px)" : "none"
                : "translateY(28px)",
              transition: "opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.5s ease",
            }}
            onMouseEnter={() => setBeforeHovered(true)}
            onMouseLeave={() => setBeforeHovered(false)}
          >
            {/* Subtle hover tint */}
            <div
              className="pointer-events-none absolute inset-0 bg-black/[0.015] transition-opacity duration-500"
              style={{ opacity: beforeHovered ? 1 : 0 }}
            />

            <p className="font-mono text-sm uppercase tracking-[0.1em] text-black/35">Before</p>
            <h3 className="mt-5 text-[3.35rem] font-semibold leading-[0.95] tracking-[-0.075em] text-black">
              Local utility chain.
            </h3>

            <div className="mt-12 space-y-4">
              {before.map((item, index) => (
                <div
                  key={item}
                  className="group/row flex items-center justify-between border-b border-black/10 pb-4 text-xl tracking-[-0.035em] text-black/58 transition-all duration-300 hover:border-black/20 hover:text-black/75 hover:pl-1"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? "none" : "translateX(-12px)",
                    transition: `opacity 0.55s cubic-bezier(0.22,1,0.36,1) ${160 + index * 80}ms, transform 0.55s cubic-bezier(0.22,1,0.36,1) ${160 + index * 80}ms, padding 0.2s ease, color 0.2s ease, border-color 0.2s ease`,
                  }}
                >
                  {item}
                  <XCircle className="h-6 w-6 flex-none text-red-500/70 transition-all duration-300 group-hover/row:scale-110 group-hover/row:text-red-500" />
                </div>
              ))}
            </div>
          </div>

          {/* ── After card ──────────────────────────────────────────── */}
          <div
            className="lb-line-card relative overflow-hidden p-8 sm:p-10"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible
                ? afterHovered ? "translateY(-4px)" : "none"
                : "translateY(28px)",
              transition: "opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) 120ms, transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.5s ease",
              boxShadow: afterHovered ? "0 20px 60px rgba(0,0,0,0.10)" : undefined,
            }}
            onMouseEnter={() => setAfterHovered(true)}
            onMouseLeave={() => setAfterHovered(false)}
          >
            {/* Green glow on hover */}
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-emerald-500/[0.04] to-transparent transition-opacity duration-500"
              style={{ opacity: afterHovered ? 1 : 0 }}
            />

            <p className="font-mono text-sm uppercase tracking-[0.1em] text-black/35">After</p>
            <h3 className="mt-5 text-[3.35rem] font-semibold leading-[0.95] tracking-[-0.075em] text-black">
              Connected browser workspace.
            </h3>

            <div className="mt-12 space-y-4">
              {after.map((item, index) => (
                <div
                  key={item}
                  className="group/row flex items-center justify-between border-b border-black/10 pb-4 text-xl tracking-[-0.035em] text-black transition-all duration-300 hover:border-black/25 hover:pl-1"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? "none" : "translateX(12px)",
                    transition: `opacity 0.55s cubic-bezier(0.22,1,0.36,1) ${280 + index * 80}ms, transform 0.55s cubic-bezier(0.22,1,0.36,1) ${280 + index * 80}ms, padding 0.2s ease, border-color 0.2s ease`,
                  }}
                >
                  {item}
                  <CheckCircle2 className="h-6 w-6 flex-none text-emerald-600/80 transition-all duration-300 group-hover/row:scale-110 group-hover/row:text-emerald-600" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
